// src/component/page/arena/ArenaPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  Alert,
  Snackbar,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Chip,
  IconButton,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";

/* =========================
   CONFIG
   ========================= */
const URL_MAIN =
  process.env.NODE_ENV === "production"
    ? "https://bigdogbone.up.railway.app"
    : "http://localhost:3002";

const ENDPOINTS = {
  status: `${URL_MAIN}/api/arena/status`,
  leaderboard: `${URL_MAIN}/api/arena/leaderboard`,
  avatar: `${URL_MAIN}/api/arena/avatar`,
  queueStart: `${URL_MAIN}/api/arena/queue/start`,
  queueResolve: `${URL_MAIN}/api/arena/queue/resolve`,
  feed: `${URL_MAIN}/api/arena/feed`, // feed global tickets / actividad
  battleFeed: `${URL_MAIN}/api/arena/battle-feed`, // feed global de batallas / economía
  // === NUEVO: feed de reinicio / payouts semanales ===
  resetFeed: `${URL_MAIN}/api/arena/reset-feed`,
};

// === NUEVO: símbolo VIP configurable ===
const VIP_SYMBOL = process.env.REACT_APP_ARENA_VIP_SYMBOL || "VIPHUESO";

// === NUEVO: símbolo/decimales SWAP configurables ===
const SWAP_SYMBOL = process.env.REACT_APP_ARENA_SWAP_SYMBOL || "SWAP.HIVE";
const SWAP_DECIMALS = Number(process.env.REACT_APP_ARENA_SWAP_DECIMALS || "2");

// formateo genérico con decimales (para tokens tipo SWAP.HIVE)
const fmtToken = (n, dec = 8) => {
  const v = Number(n || 0);
  if (!isFinite(v)) return "0";
  if (dec === 0) return new Intl.NumberFormat().format(Math.floor(v));
  return v.toFixed(dec);
};

// SPA route to Burn (Album tab 6)
const BURN_PATH = "/game/album?page=6";

/* ===== Bone Power Table ===== */
const BONE_VALUES = {
  general: { C: 200, R: 600, Z: 1400, E: 2400, A: 3000, L: 5000 },
  especial: { C: 400, R: 1200, Z: 2800, E: 4800, A: 6000, L: 10000 },
};
const RAREZAS = ["C", "R", "Z", "E", "A", "L"];

const fmt = (n) => new Intl.NumberFormat().format(Number(n || 0));
const fmt3 = (n) => Number(n || 0).toFixed(3);

const fmtVip = (x) => {
  const v = Number(x || 0);
  if (v === 0) return "0";
  if (v >= 1) return fmt(v);
  return fmt3(v);
};

/* === Helpers === */
function getCookie(name) {
  try {
    return document.cookie
      .split("; ")
      .find((r) => r.startsWith(`${name}=`))
      ?.split("=")[1];
  } catch {
    return undefined;
  }
}
function getAuthHeaders() {
  const t =
    getCookie("token_bdbg") ||
    getCookie("token") ||
    localStorage.getItem("token_bdbg") ||
    localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}`, "x-token": t } : {};
}
async function fetchJson(url, options = {}) {
  const res = await fetch(url, options);
  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok) {
    const msg = data?.msg || data?.error || `HTTP ${res.status}`;
    const e = new Error(msg);
    e.response = { status: res.status, data };
    throw e;
  }
  return data ?? {};
}

/* === Avatars (Cloudinary) === */
const AVATARS = [
  {
    id: "av1",
    name: "Avatar-1",
    img: "https://res.cloudinary.com/dekoxefky/image/upload/v1761103436/BDB1_dwnssn.png",
  },
  {
    id: "av2",
    name: "Avatar-2",
    img: "https://res.cloudinary.com/dekoxefky/image/upload/v1761103908/BDB2_slr2pj.png",
  },
  {
    id: "av3",
    name: "Avatar-3",
    img: "https://res.cloudinary.com/dekoxefky/image/upload/v1761103909/BDB3_ejwzgm.png",
  },
];

// fallback avatar helper for leaderboard rows
function getAvatarForRow(row) {
  if (row?.avatar && row.avatar.img) {
    return {
      img: row.avatar.img,
      name: row.avatar.name || row.nombre || "avatar",
    };
  }
  if (row?.avatar && row.avatar.id) {
    const local = AVATARS.find((a) => a.id === row.avatar.id);
    if (local) {
      return { img: local.img, name: local.name || row.nombre || "avatar" };
    }
  }
  return {
    img: AVATARS[0].img,
    name: AVATARS[0].name,
  };
}

// PP a mostrar en leaderboard
function getRowPP(row) {
  if (typeof row?.pp === "number") return row.pp;
  if (typeof row?.ppActivo === "number") return row.ppActivo;
  if (typeof row?.power === "number") return row.power;
  if (typeof row?.preview?.pp === "number") return row.preview.pp;
  if (typeof row?.estimatedPower?.total === "number") return row.estimatedPower.total;
  return 0;
}

// SWAP preview helper para el leaderboard (flexible según responda el backend)
// SWAP preview helper (lee raíz y variantes) — admite decimales/símbolo dinámicos
function getRowSwapPreview(row, dec = SWAP_DECIMALS, sym = SWAP_SYMBOL) {
  try {
    // 0) raíz: row.swap === number (formato backend actual)
    if (typeof row?.swap === "number") {
      return fmtToken(row.swap, dec);
    }

    // 1) row.preview.swap === number
    const p = row?.preview || {};
    if (typeof p.swap === "number") {
      return fmtToken(p.swap, dec);
    }

    // 2) row.preview.swap.amount (+ opcional .decimals)
    if (p.swap && typeof p.swap.amount === "number") {
      return fmtToken(p.swap.amount, Number(p.swap.decimals ?? dec));
    }

    // 3) row.preview.swap[sym] === number
    if (p.swap && typeof p.swap[sym] === "number") {
      return fmtToken(p.swap[sym], dec);
    }

    // 4) row.preview.swapAmount === number
    if (typeof p.swapAmount === "number") {
      return fmtToken(p.swapAmount, dec);
    }
  } catch {}

  return fmtToken(0, dec);
}

function pickAvatarForUser(u) {
  if (u?.avatar?.img) {
    return { img: u.avatar.img, name: u.avatar.name || u.nombre || "avatar" };
  }
  if (u?.avatar?.id) {
    const local = AVATARS.find((a) => a.id === u.avatar.id);
    if (local) return { img: local.img, name: local.name || u.nombre || "avatar" };
  }
  return { img: AVATARS[0].img, name: u?.nombre || "Player" };
}

/* === Battle animation (video con sonido) === */
const BATTLE_ANIM =
  "https://res.cloudinary.com/dekoxefky/video/upload/v1761188757/Mi_video_1_pyhe4b.mp4";

/* === Ticket GIF (pedido) === */
const TICKET_GIF =
  "https://www.bigdogbonegame.com/static/media/ticket-stake-brillo.ecb5844af3e158141cef.gif";

/* === Porcentaje 50% → pool / 50% → burn (si backend no envía poolPercent) === */
const DEFAULT_POOL_PERCENT = 0.5;

export default function ArenaPage() {
  const [loading, setLoading] = useState(true);
  const [energyCost, setEnergyCost] = useState(5);
  const [entryFee, setEntryFee] = useState(500);
  const [energia, setEnergia] = useState(0);
  const [hueso, setHueso] = useState(0);
  const [username, setUsername] = useState("");
  const [perfil, setPerfil] = useState({
    avatar: AVATARS[0],
    pp: 0,
    mmr: 1000,
    wins: 0,
    losses: 0,
    streak: 0,
    tickets: 0,
    userId: null,
    nombre: "",
    battlesThisWeek: 0,
    activityTicketsThisWeek: 0,
  });
  const [pool, setPool] = useState(0);
  const [poolPercent, setPoolPercent] = useState(DEFAULT_POOL_PERCENT);
  const [season, setSeason] = useState("");
  const [queueSize, setQueueSize] = useState(0);
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "info",
  });

  const [leader, setLeader] = useState([]);
  const [vipWeekly, setVipWeekly] = useState(0);
  const [vipSymbol, setVipSymbol] = useState(VIP_SYMBOL);
  const [swapSymbol, setSwapSymbol] = useState(SWAP_SYMBOL);
  const [swapDecimals, setSwapDecimals] = useState(SWAP_DECIMALS);


  // Quemado (del backend)
  const [burnedWeek, setBurnedWeek] = useState(0);
  const [burnedTotal, setBurnedTotal] = useState(0);

  // If backend includes rarity counts
  const [userCounts, setUserCounts] = useState(null);

  // feed global Arena (tickets / actividad)
  const [arenaFeed, setArenaFeed] = useState([]); // [{ts,user,qty,razon,detalle,...}]

  // feed global de batallas / economía
  const [battleFeed, setBattleFeed] = useState([]); // [{ts,type,winner,players:[...],poolAdded,burnAdded}]

  // === NUEVO: feed de reinicio / payouts semanales
  const [resetFeed, setResetFeed] = useState([]); // [{ts,type|razon,seasonKey,huesoSent,ticketsSent,vipSent,detalle,...}]

  // progreso global para desbloquear el pool
  const [unlockProgress, setUnlockProgress] = useState({
    current: 0,
    goal: 0,
    unlocked: false,
  });

  // Battle modal / anim
  const [battleOpen, setBattleOpen] = useState(false);
  const [battlePhase, setBattlePhase] = useState("anim"); // "anim" | "result"
  const [battleCountdown, setCountdown] = useState(12);
  const [battleResult, setBattleResult] = useState(null);

  const [animStatusText, setAnimStatusText] = useState("Searching opponent...");

  // Reward modal
  const [rewardOpen, setRewardOpen] = useState(false);
  const [rewardInfo, setRewardInfo] = useState(null);

  // Video controls
  const videoRef = useRef(null);

  // reward pendiente hasta que termine animación
  const pendingRewardRef = useRef(null);

  const [muted, setMuted] = useState(false);

  const ANIM_MS = 12000; // 12s

  const animTimerRef = useRef(null);
  const [animDone, setAnimDone] = useState(false);
  const [resultReady, setResultReady] = useState(false);
  const resultBufferRef = useRef(null);

  const notify = (msg, severity = "info") =>
    setSnack({ open: true, msg, severity });
  const closeSnack = () =>
    setSnack((s) => ({
      ...s,
      open: false,
    }));

  // ===== loaders =====
  const loadStatus = async () => {
    try {
      setLoading(true);
      const d = await fetchJson(ENDPOINTS.status, {
        headers: { ...getAuthHeaders() },
      });

      setEnergyCost(Number(d.energyCost ?? 2));
      setEntryFee(Number(d.entryFee ?? 10));
      setEnergia(Number(d.energia ?? 0));
      setHueso(Number(d.hueso ?? 0));
      setPool(Number(d.pool ?? 0));
      setSeason(d.season || "");
      setQueueSize(Number(d.queueSize ?? 0));

      setBurnedWeek(Number(d.burnedWeek ?? 0));
      setBurnedTotal(Number(d.burnedTotal ?? 0));

      if (d.unlockProgress) {
        setUnlockProgress({
          current: Number(d.unlockProgress.current || 0),
          goal: Number(d.unlockProgress.goal || 0),
          unlocked: Boolean(d.unlockProgress.unlocked),
        });
      }

      const uname = d?.perfil?.nombre || d?.user?.nombre || "";
      if (uname) setUsername(uname);

      if (d.perfil) {
        let avatar = d.perfil?.avatar;
        if (avatar?.id) {
          const local = AVATARS.find((a) => a.id === avatar.id);
          if (local) avatar = local;
        }
        setPerfil((p) => ({
          ...p,
          ...d.perfil,
          avatar: avatar?.img
            ? avatar
            : AVATARS.find((a) => a.id === avatar?.id) || AVATARS[0],
          battlesThisWeek: d.perfil.battlesThisWeek ?? p.battlesThisWeek,
          activityTicketsThisWeek:
            d.perfil.activityTicketsThisWeek ?? p.activityTicketsThisWeek,
        }));
      }

      if (d.userCounts) setUserCounts(d.userCounts);
      if (typeof d.poolPercent === "number") {
        setPoolPercent(Number(d.poolPercent));
      } else {
        setPoolPercent(DEFAULT_POOL_PERCENT);
      }
    } catch (e) {
      notify(e?.message || "Unauthorized in Arena", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboard = async () => {
    try {
      const d = await fetchJson(`${ENDPOINTS.leaderboard}?size=200`, {
        headers: { ...getAuthHeaders() },
      });
      if (Array.isArray(d?.data)) setLeader(d.data);

      if (typeof d?.vipWeekly === "number") setVipWeekly(Number(d.vipWeekly));
      if (typeof d?.vipSymbol === "string" && d.vipSymbol.trim()) {
        setVipSymbol(d.vipSymbol.trim());
      }
      if (typeof d?.pool === "number") setPool(Number(d.pool));
      if (typeof d?.burnedWeek === "number") setBurnedWeek(Number(d.burnedWeek));
      if (typeof d?.burnedTotal === "number")
        setBurnedTotal(Number(d.burnedTotal));

      // Backend puede mandar símbolo/decimales efectivos para SWAP
if (typeof d?.swapSymbol === "string" && d.swapSymbol.trim()) {
  setSwapSymbol(d.swapSymbol.trim());
}
if (Number.isFinite(d?.swapDecimals)) {
  setSwapDecimals(Number(d.swapDecimals));
}


      if (d.unlockProgress) {
        setUnlockProgress({
          current: Number(d.unlockProgress.current || 0),
          goal: Number(d.unlockProgress.goal || 0),
          unlocked: Boolean(d.unlockProgress.unlocked),
        });
      }
    } catch {
      /* silent */
    }
  };

  const loadFeed = async () => {
    try {
      const d = await fetchJson(ENDPOINTS.feed, {
        headers: { ...getAuthHeaders() },
      });
      if (Array.isArray(d?.feed)) {
        const sorted = [...d.feed].sort((a, b) => b.ts - a.ts).slice(0, 20);
        setArenaFeed(sorted);
      }
    } catch {
      /* silent */
    }
  };

  const loadBattleFeed = async () => {
    try {
      const d = await fetchJson(ENDPOINTS.battleFeed, {
        headers: { ...getAuthHeaders() },
      });
      if (Array.isArray(d?.feed)) {
        const sorted = [...d.feed].sort((a, b) => b.ts - a.ts).slice(0, 20);
        setBattleFeed(sorted);
      }
      if (typeof d?.pool === "number") setPool(Number(d.pool));
      if (typeof d?.burnedWeek === "number") setBurnedWeek(Number(d.burnedWeek));
      if (typeof d?.burnedTotal === "number")
        setBurnedTotal(Number(d.burnedTotal));
    } catch {
      /* silent */
    }
  };

  // === NUEVO: cargar feed de reinicio / payouts semanales
  const loadResetFeed = async () => {
    try {
      const d = await fetchJson(ENDPOINTS.resetFeed, {
        headers: { ...getAuthHeaders() },
      });
      if (Array.isArray(d?.feed)) {
        const sorted = [...d.feed].sort((a, b) => b.ts - a.ts).slice(0, 20);
        setResetFeed(sorted);
      }
    } catch {
      /* silent */
    }
  };

  // on mount
  useEffect(() => {
    loadStatus();
    loadLeaderboard();
    loadFeed();
    loadBattleFeed();
    loadResetFeed(); // NUEVO
  }, []);

  // auto-refresh cada ~20s
  useEffect(() => {
    const id = setInterval(() => {
      loadStatus();
      loadLeaderboard();
      loadFeed();
      loadBattleFeed();
      loadResetFeed(); // NUEVO
    }, 20000);
    return () => clearInterval(id);
  }, []);

  /* =============== Avatar =============== */
  const setAvatar = async (avatarId) => {
    try {
      await fetchJson(ENDPOINTS.avatar, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ avatarId }),
      });
      const chosen = AVATARS.find((a) => a.id === avatarId) || AVATARS[0];
      setPerfil((p) => ({ ...p, avatar: chosen }));
      notify("Avatar updated", "success");
    } catch (e) {
      const chosen = AVATARS.find((a) => a.id === avatarId);
      if (chosen) setPerfil((p) => ({ ...p, avatar: chosen }));
      notify(e?.message || "Avatar change failed", "error");
    }
  };

  /* =============== Battle animation (countdown + fin garantizado) =============== */
  useEffect(() => {
    if (!battleOpen || battlePhase !== "anim") return;

    setAnimStatusText("Searching opponent...");

    setAnimDone(false);
    setResultReady(false);
    resultBufferRef.current = null;

    const seconds = Math.ceil(ANIM_MS / 1000);
    setCountdown(seconds);

    const phaseTimer = setTimeout(() => {
      setAnimStatusText("Fighting...");
    }, 10000);

    const tick = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(tick);
          return 0;
        }
        return c - 1;
      });
    }, 1000);

    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    animTimerRef.current = setTimeout(() => {
      setAnimDone(true);
    }, ANIM_MS);

    return () => {
      clearTimeout(phaseTimer);
      clearInterval(tick);
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battleOpen, battlePhase, ANIM_MS]);

  // === MATCH RESOLVE
  useEffect(() => {
    const runResolve = async () => {
      try {
        const d = await fetchJson(ENDPOINTS.queueResolve, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({}),
        });

        let myPp = perfil.pp;
        let myMmr = perfil.mmr;
        let outcome = { title: "Result", detail: null };

        const result = d?.result;
        if (result) {
          if (result.type === "pvp") {
            const me =
              result?.a?.userId &&
              perfil?.userId &&
              result.a.userId === perfil.userId
                ? result.a
                : result.b;

            const opp =
              me && result?.a?.userId === me.userId ? result.b : result.a;

            let before = myPp;
            let consumed = 0,
              refund = 0,
              final = myPp;
            if (me?.decay) {
              before = Number(me.before ?? myPp);
              consumed = Number(me.decay.consumed ?? 0);
              refund = Number(me.decay.refund ?? 0);
              final = Number(me.decay.final ?? myPp);
            }

            const meAvatar = pickAvatarForUser(me);
            const oppAvatar = pickAvatarForUser(opp);

            const iWon = result.winner === (me?.userId ?? perfil.userId);
            const winnerName = iWon
              ? me?.nombre || "You"
              : opp?.nombre || "Opponent";

            myPp = final;
            myMmr = Number(me?.mmr || myMmr);

            outcome = {
              title: "PvP Battle",
              detail: {
                type: "pvp",
                rounds: result.rounds?.length ?? 3,
                won: iWon,
                winnerName,
                matchup: {
                  winnerId: result.winner,
                  me: {
                    id: me?.userId,
                    name: me?.nombre || "You",
                    avatarUrl: meAvatar.img,
                    mmr: Number(me?.mmr || myMmr),
                  },
                  opp: {
                    id: opp?.userId,
                    name: opp?.nombre || "Opponent",
                    avatarUrl: oppAvatar.img,
                    mmr: Number(opp?.mmr || 0),
                  },
                },
                pp: {
                  before,
                  consumed,
                  refund,
                  final,
                  lost: Math.max(0, consumed - refund),
                },
              },
            };
          } else if (result.type === "bot") {
            let before = myPp;
            let consumed = 0,
              refund = 0,
              final = myPp;

            if (result?.player?.decay) {
              before = Number(result.player.before ?? myPp);
              consumed = Number(result.player.decay.consumed ?? 0);
              refund = Number(result.player.decay.refund ?? 0);
              final = Number(result.player.decay.final ?? myPp);
            }

            const me = result.player || { userId: perfil.userId, nombre: username };
            const meAvatar = pickAvatarForUser(me);

            myPp = final;
            myMmr = Number(result?.player?.mmr || myMmr);

            outcome = {
              title: "Battle vs BOT",
              detail: {
                type: "bot",
                rounds: result.rounds?.length ?? 3,
                won: result.winner === (perfil.userId ?? "BOT"),
                winnerName:
                  result.winner === (perfil.userId ?? "BOT")
                    ? username || "You"
                    : "BOT",
                matchup: {
                  winnerId: result.winner,
                  me: {
                    id: me?.userId,
                    name: me?.nombre || "You",
                    avatarUrl: meAvatar.img,
                    mmr: Number(result?.player?.mmr || myMmr),
                  },
                  opp: {
                    id: "BOT",
                    name: result?.bot?.name || "BOT",
                    avatarUrl: AVATARS[1].img,
                    mmr: Number(result?.bot?.mmr || 0),
                  },
                },
                pp: {
                  before,
                  consumed,
                  refund,
                  final,
                  lost: Math.max(0, consumed - refund),
                },
              },
            };
          }
        }

        const reward = result?.reward;

        if (reward && outcome?.detail?.won) {
          pendingRewardRef.current = reward;
        } else {
          pendingRewardRef.current = null;
        }

        const updatedBattlesThisWeek =
          result?.player?.battlesThisWeek ??
          result?.a?.battlesThisWeek ??
          perfil.battlesThisWeek;

        const updatedActivityTicketsThisWeek =
          result?.player?.activityTicketsThisWeek ??
          result?.a?.activityTicketsThisWeek ??
          perfil.activityTicketsThisWeek;

        setPerfil((p) => ({
          ...p,
          pp: myPp,
          mmr: myMmr,
          battlesThisWeek: updatedBattlesThisWeek,
          activityTicketsThisWeek: updatedActivityTicketsThisWeek,
        }));

        await loadLeaderboard();
        await loadFeed();
        await loadBattleFeed();
        await loadStatus();

        resultBufferRef.current = outcome;
        setResultReady(true);
      } catch (e) {
        resultBufferRef.current = {
          title: "Error",
          detail: {
            won: false,
            rounds: 0,
            winnerName: "—",
            matchup: null,
            pp: {
              before: perfil.pp,
              consumed: 0,
              refund: 0,
              final: perfil.pp,
              lost: 0,
            },
          },
        };
        setResultReady(true);
        notify(e?.message || "Battle resolve failed", "error");
      }
    };

    if (animDone && !resultReady) {
      runResolve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animDone, resultReady]);

  useEffect(() => {
    if (animDone && resultReady && resultBufferRef.current) {
      setBattleResult(resultBufferRef.current);
      setBattlePhase("result");

      if (pendingRewardRef.current) {
        setRewardInfo(pendingRewardRef.current);
        setTimeout(() => setRewardOpen(true), 200);
        pendingRewardRef.current = null;
      }
    }
  }, [animDone, resultReady]);

  const tryPlayVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
    v.play().catch(() => {});
  };

  const joinBattle = async () => {
    setBattleOpen(true);
    setBattlePhase("anim");
    setBattleResult(null);
    setRewardOpen(false);
    setRewardInfo(null);
    setMuted(false);

    setAnimDone(false);
    setResultReady(false);
    resultBufferRef.current = null;
    pendingRewardRef.current = null;

    setTimeout(tryPlayVideo, 50);

    try {
      await fetchJson(ENDPOINTS.queueStart, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ nftId: null }),
      });
    } catch (e) {
      setBattleOpen(false);
      notify(e?.message || "Could not enter queue", "error");
      return;
    }
  };

  /* =============== Estimated power (optional) =============== */
  const estimatedPower = useMemo(() => {
    if (!userCounts) return null;
    const g = userCounts.general || {};
    const e = userCounts.especial || {};
    let totalG = 0,
      totalE = 0;
    for (const r of RAREZAS) {
      totalG += (g[r] || 0) * (BONE_VALUES.general[r] || 0);
    }
    for (const r of RAREZAS) {
      totalE += (e[r] || 0) * (BONE_VALUES.especial[r] || 0);
    }
    return { general: totalG, especial: totalE, total: totalG + totalE };
  }, [userCounts]);

  /* =========================
     UI
     ========================= */
  const bgStyle = {
    minHeight: "100%",
    width: "100%",
    position: "relative",
    backgroundImage:
      "url(https://res.cloudinary.com/dpwv380jp/image/upload/v1754942090/Blue_And_Black_Modern_YouTube_Shorts_Outro_Mobile_Video_1_rmzcny.gif)",
    backgroundPosition: "center",
    backgroundSize: "cover",
    padding: "16px",
  };

  const cardProps = {
    sx: {
      p: 2,
      mb: 2,
      bgcolor: "rgba(255,255,255,0.96)",
      color: "#000",
      borderRadius: 2,
      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    },
  };
  const btnPrimary = {
    variant: "contained",
    sx: {
      bgcolor: "#ffcc00",
      color: "#000",
      "&:hover": { bgcolor: "#ffdb4d" },
      textTransform: "none",
      borderRadius: 2,
      fontWeight: 800,
    },
  };
  const btnGhost = {
    variant: "outlined",
    sx: {
      color: "#000",
      textTransform: "none",
      borderRadius: 2,
      borderColor: "#222",
      "&:hover": { borderColor: "#000" },
    },
  };

  const poolShare = Math.round((poolPercent || DEFAULT_POOL_PERCENT) * 100);

  const unlockPct = useMemo(() => {
    if (!unlockProgress.goal || unlockProgress.goal <= 0) return 0;
    return Math.min(
      100,
      Math.floor((unlockProgress.current / unlockProgress.goal) * 100)
    );
  }, [unlockProgress]);

  return (
    <Box sx={bgStyle}>
      <Box
        sx={{
          maxWidth: 1080,
          mx: "auto",
          "& .MuiPaper-root": { color: "#000" },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 900, color: "#ffb300ff" }}
          >
            🐕‍🦺 Dog Arena
          </Typography>
          {username && (
            <Chip
              label={`Player: ${username}`}
              color="warning"
              sx={{ fontWeight: 700 }}
            />
          )}
        </Stack>

        <Grid container spacing={2}>
          {/* Left column */}
          <Grid item xs={12} md={7}>
            <Paper {...cardProps}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Profile
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 96,
                    height: 96,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid #ddd",
                  }}
                >
                  <img
                    src={perfil?.avatar?.img || AVATARS[0].img}
                    alt="avatar"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      e.currentTarget.src = AVATARS[0].img;
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="body1">
                    <b>{username || perfil?.nombre || "Player"}</b>
                  </Typography>

                  <Tooltip
                    title="MMR indicates your skill. It rises with wins and falls with losses."
                    arrow
                  >
                    <Typography
                      variant="body1"
                      sx={{ cursor: "help" }}
                    >
                      MMR: <b>{fmt(perfil.mmr)}</b>
                    </Typography>
                  </Tooltip>

                  <Tooltip
                    title="PP (Special Power) comes from burning NFTs (HUESO value). It decays after each battle."
                    arrow
                  >
                    <Typography
                      variant="body1"
                      sx={{ cursor: "help" }}
                    >
                      PP: <b>{fmt(perfil.pp)}</b>
                    </Typography>
                  </Tooltip>

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 0.5, flexWrap: "wrap" }}
                  >
                    <Tooltip title="Total wins." arrow>
                      <Typography
                        variant="body2"
                        sx={{ cursor: "help" }}
                      >
                        Wins: <b>{fmt(perfil.wins)}</b>
                      </Typography>
                    </Tooltip>
                    <Typography variant="body2">·</Typography>
                    <Tooltip title="Total losses." arrow>
                      <Typography
                        variant="body2"
                        sx={{ cursor: "help" }}
                      >
                        Losses: <b>{fmt(perfil.losses)}</b>
                      </Typography>
                    </Tooltip>
                    <Typography variant="body2">·</Typography>
                    <Tooltip title="Current win streak." arrow>
                      <Typography
                        variant="body2"
                        sx={{ cursor: "help" }}
                      >
                        Streak: <b>{fmt(perfil.streak)}</b>
                      </Typography>
                    </Tooltip>
                  </Stack>

                  {/* actividad semanal */}
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 0.5, flexWrap: "wrap" }}
                  >
                    <Tooltip
                      title="Battles you played this week (counts even if you lose)."
                      arrow
                    >
                      <Typography
                        variant="body2"
                        sx={{ cursor: "help" }}
                      >
                        Weekly Battles:{" "}
                        <b>{fmt(perfil.battlesThisWeek || 0)}</b>
                      </Typography>
                    </Tooltip>
                    <Typography variant="body2">·</Typography>
                    <Tooltip
                      title="Tickets already claimed from activity this week."
                      arrow
                    >
                      <Typography
                        variant="body2"
                        sx={{ cursor: "help" }}
                      >
                        Activity Tickets:{" "}
                        <b>{fmt(perfil.activityTicketsThisWeek || 0)}</b>
                      </Typography>
                    </Tooltip>
                  </Stack>
                </Box>

                {/* Avatar picker */}
                <Box sx={{ ml: "auto" }}>
                  <Stack direction="row" spacing={1}>
                    {AVATARS.map((av) => (
                      <Card
                        key={av.id}
                        sx={{ width: 72, borderRadius: 2 }}
                      >
                        <CardActionArea
                          onClick={() => setAvatar(av.id)}
                        >
                          <CardMedia
                            component="img"
                            image={av.img}
                            alt={av.name}
                          />
                        </CardActionArea>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Paper>

            <Paper {...cardProps}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Resources & Economy
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Tooltip
                      title="Energy available to join battles."
                      arrow
                    >
                      <Typography sx={{ cursor: "help" }}>
                        ⚡ Energy: <b>{fmt(energia)}</b>
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      title="Your HUESO balance (off-chain mirror of on-chain token)."
                      arrow
                    >
                      <Typography sx={{ cursor: "help" }}>
                        🦴 HUESO: <b>{fmt(hueso)}</b>
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      title="HUESO entry fee per battle."
                      arrow
                    >
                      <Typography sx={{ cursor: "help" }}>
                        💸 Entry Fee: <b>{fmt(entryFee)}</b>
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      title="Energy cost per battle entry."
                      arrow
                    >
                      <Typography sx={{ cursor: "help" }}>
                        🎯 Energy Cost: <b>{fmt(energyCost)}</b>
                      </Typography>
                    </Tooltip>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography>
                      🏦 Weekly Pool: <b>{fmt(pool)}</b> HUESO
                    </Typography>
                    <Typography>
                      📊 Fee Split: <b>{poolShare}%</b> Pool /{" "}
                      <b>{100 - poolShare}%</b> Burn
                    </Typography>
                    <Typography>
                      🔥 Burn (week): <b>{fmt(burnedWeek)}</b> HUESO
                    </Typography>
                    <Typography>
                      🔥 Burn (total): <b>{fmt(burnedTotal)}</b> HUESO
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              {/* bloque de progreso de desbloqueo del pool */}
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid rgba(0,0,0,0.15)",
                  backgroundColor: "rgba(255,255,255,0.6)",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700 }}
                  >
                    Weekly Unlock
                  </Typography>

                  <Chip
                    size="small"
                    color={
                      unlockProgress.unlocked ? "success" : "error"
                    }
                    label={
                      unlockProgress.unlocked
                        ? "UNLOCKED"
                        : "LOCKED"
                    }
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>

                <Typography
                  variant="body2"
                  sx={{ mb: 1 }}
                >
                  Battles played this week (all players):{" "}
                  <b>
                    {fmt(unlockProgress.current)}/
                    {fmt(unlockProgress.goal || 0)}
                  </b>
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={unlockPct}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "rgba(0,0,0,0.1)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 4,
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    opacity: 0.7,
                  }}
                >
                  Pool payouts only happen if unlocked.
                </Typography>
              </Box>
            </Paper>

            {/* Bone power table + Burn link */}
            <Paper {...cardProps}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Power per NFT (HUESO)
              </Typography>
              <Typography
                variant="body2"
                sx={{ opacity: 0.8, mb: 1 }}
              >
                Each NFT contributes <b>power</b> equal to its HUESO
                value when burned.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5 }}
                  >
                    General
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid #ddd",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        p: 1,
                        bgcolor: "#f7f7f7",
                        fontWeight: 700,
                      }}
                    >
                      <span>Rarity</span>
                      <span>HUESO (Power)</span>
                    </Box>
                    {RAREZAS.map((r) => (
                      <Box
                        key={`g-${r}`}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          p: 1,
                          borderTop: "1px solid #eee",
                        }}
                      >
                        <span>{r}</span>
                        <span>{fmt(BONE_VALUES.general[r])}</span>
                      </Box>
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5 }}
                  >
                    Special
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid #ddd",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        p: 1,
                        bgcolor: "#f7f7f7",
                        fontWeight: 700,
                      }}
                    >
                      <span>Rarity</span>
                      <span>HUESO (Power)</span>
                    </Box>
                    {RAREZAS.map((r) => (
                      <Box
                        key={`e-${r}`}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          p: 1,
                          borderTop: "1px solid #eee",
                        }}
                      >
                        <span>{r}</span>
                        <span>{fmt(BONE_VALUES.especial[r])}</span>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>

              {/* Estimated power from counts (if provided) */}
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 0.5 }}
                >
                  Estimated Accumulated Power
                </Typography>
                {estimatedPower ? (
                  <Stack
                    direction="row"
                    spacing={3}
                    flexWrap="wrap"
                  >
                    <Typography>
                      General:{" "}
                      <b>{fmt(estimatedPower.general)}</b>
                    </Typography>
                    <Typography>
                      Special:{" "}
                      <b>{fmt(estimatedPower.especial)}</b>
                    </Typography>
                    <Typography>
                      Total:{" "}
                      <b>{fmt(estimatedPower.total)}</b>
                    </Typography>
                  </Stack>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.7 }}
                  >
                    N/A — will be calculated automatically when
                    the backend exposes rarity totals.
                  </Typography>
                )}
              </Box>

              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                  component={RouterLink}
                  to={BURN_PATH}
                  variant="contained"
                  color="warning"
                  sx={{
                    color: "#000",
                    fontWeight: 800,
                    textTransform: "uppercase",
                    borderRadius: 2,
                  }}
                >
                  🔥 Open Burn System
                </Button>

                <Typography
                  variant="body2"
                  sx={{
                    alignSelf: "center",
                    opacity: 0.8,
                  }}
                >
                  Your current <b>PP</b>: {fmt(perfil.pp)}
                </Typography>
              </Stack>
            </Paper>

            <Paper {...cardProps}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems="center"
              >
                <Button
                  {...btnPrimary}
                  size="large"
                  onClick={joinBattle}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Enter Battle"}
                </Button>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.8 }}
                >
                  If no opponent is found, a BOT with similar
                  power will be created. Power decays each battle.
                </Typography>
              </Stack>
            </Paper>
          </Grid>

          {/* Right column */}
          <Grid item xs={12} md={5}>
            <Paper {...cardProps}>
              <Typography variant="h6">Status</Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 1, flexWrap: "wrap" }}
              >
                <Typography variant="body2">In Queue:</Typography>
                <Chip
                  label={fmt(queueSize)}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontWeight: 700,
                    color: "#000",
                    borderColor: "rgba(0,0,0,0.4)",
                    bgcolor: "rgba(255,255,255,0.8)",
                    lineHeight: 1.1,
                    height: 24,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ ml: 2 }}
                >
                  Season:
                </Typography>
                <Chip
                  label={season || "-"}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontWeight: 700,
                    color: "#000",
                    borderColor: "rgba(0,0,0,0.4)",
                    bgcolor: "rgba(255,255,255,0.8)",
                    lineHeight: 1.1,
                    height: 24,
                  }}
                />
              </Stack>

              <Stack
                direction="row"
                spacing={2}
                sx={{
                  mt: 2,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Button
                  {...btnPrimary}
                  onClick={() => {
                    loadStatus();
                    loadFeed();
                    loadBattleFeed();
                    loadResetFeed(); // NUEVO
                  }}
                >
                  🔄 Refresh
                </Button>

                <Button
                  {...btnPrimary}
                  onClick={() => {
                    loadLeaderboard();
                    loadFeed();
                    loadBattleFeed();
                    loadResetFeed(); // NUEVO
                  }}
                >
                  🏆 Update Ranking
                </Button>
              </Stack>
            </Paper>

            {/* Arena Activity Feed */}
            <Paper
              {...cardProps}
              sx={{
                ...cardProps.sx,
                maxHeight: 260,
                overflowY: "auto",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#000", fontWeight: 700 }}
                >
                  Arena Activity
                </Typography>

                <Button
                  {...btnPrimary}
                  size="small"
                  sx={{
                    ...btnPrimary.sx,
                    px: 1.5,
                    py: 0.5,
                    fontSize: "0.7rem",
                    lineHeight: 1.2,
                  }}
                  onClick={() => {
                    loadFeed();
                    loadBattleFeed();
                    loadResetFeed(); // refresca los 3
                  }}
                >
                  REFRESH FEED
                </Button>
              </Stack>

              {arenaFeed.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.6, color: "#000" }}
                >
                  No recent rewards yet.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {arenaFeed.map((log, idx) => {
                    const dateStr = new Date(log.ts).toLocaleTimeString(
                      [],
                      { hour: "2-digit", minute: "2-digit" }
                    );

                    let label = "";
                    if (log.razon === "WIN_DROP") {
                      label = "Win reward";
                    } else if (log.razon === "ACTIVITY") {
                      label = "Activity bonus";
                    } else if (log.razon === "POOL_PAYOUT") {
                      label = "Weekly payout";
                    } else if (log.razon === "VIP_PAYOUT") {
                      label = "VIP weekly payout";
                    } else if (/_PAYOUT$/.test(String(log.razon || "")) && log.symbol) {
                      // NUEVO: cualquier payout de token (ej: SWAP_PAYOUT)
                      label = `${log.symbol} payout`;
                    } else {
                      label = log.razon || "Reward";
                    }

                    const isVipPayout = log.razon === "VIP_PAYOUT";

                    return (
                      <Box
                        key={idx}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          border: "1px solid rgba(0,0,0,0.1)",
                          backgroundColor:
                            "rgba(255,255,255,0.6)",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: "#000",
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          {dateStr}{" "}
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#000",
                              ml: 1,
                            }}
                          >
                            {log.user}
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                ml: 0.5,
                                fontWeight: 400,
                                opacity: 0.6,
                                color: "#000",
                              }}
                            >
                              ({label})
                            </Typography>
                          </Typography>
                        </Typography>

                        {/* Render TOKEN vs VIP vs Tickets */}
                        {(log.symbol && /_PAYOUT$/.test(String(log.razon || ""))) ? (
                          // NUEVO: pagos de tokens (ej: SWAP.HIVE)
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              color: "#000",
                              fontWeight: 700,
                              ml: 4.5,
                            }}
                          >
                            💱 +{fmtToken(log.qty, swapDecimals)} {log.symbol}

                          </Typography>
                        ) : isVipPayout ? (
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              color: "#000",
                              fontWeight: 700,
                              ml: 4.5,
                            }}
                          >
                            🍖 +{fmtVip(log.qty)} {vipSymbol}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              color: "#000",
                              fontWeight: 600,
                              ml: 4.5,
                            }}
                          >
                            <img
                              src={TICKET_GIF}
                              alt="ticket"
                              style={{
                                width: 20,
                                height: 20,
                                objectFit: "contain",
                                marginRight: 4,
                              }}
                            />
                            +{fmt(log.qty)} ticket{Number(log.qty) === 1 ? "" : "s"}
                          </Typography>
                        )}

                        {log.detalle && (
                          <Box sx={{ ml: 4.5, mt: 0.25 }}>
                            <Chip
                              size="small"
                              color="warning"
                              label={log.detalle}
                              sx={{ height: 22, fontWeight: 800 }}
                            />
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Paper>

            {/* Battle / Economy Feed */}
            <Paper
              {...cardProps}
              sx={{
                ...cardProps.sx,
                maxHeight: 260,
                overflowY: "auto",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#000",
                  fontWeight: 700,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  rowGap: 1,
                }}
              >
                <span>Battle Log / Economy</span>
                <Chip
                  size="small"
                  label={`Burn Wk: ${fmt(
                    burnedWeek
                  )} | Pool: ${fmt(pool)}`}
                  sx={{ fontWeight: 700 }}
                  color="warning"
                />
              </Typography>

              {battleFeed.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.6, color: "#000" }}
                >
                  No recent battles logged.
                </Typography>
              ) : (
                <Stack spacing={1}>
{battleFeed.map((ev, idx) => {
  const dateStr = new Date(ev.ts).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const vsLine = ev.players
    ? ev.players
        .map((p) => `${p.nombre} ${p.won ? "✅" : "❌"}`)
        .join("  vs  ")
    : "";

  return (
    <Box
      key={idx}
      sx={{
        p: 1,
        borderRadius: 1,
        border: "1px solid rgba(0,0,0,0.1)",
        backgroundColor: "rgba(255,255,255,0.6)",
      }}
    >
      {/* cabecera */}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: "#000",
          display: "block",
          mb: 0.5,
        }}
      >
        {dateStr}{" "}
        <Typography
          component="span"
          variant="body2"
          sx={{ fontWeight: 700, color: "#000", ml: 1 }}
        >
          {ev.type === "bot" ? "BOT Match" : "PvP Match"}{" "}
          <Typography
            component="span"
            variant="body2"
            sx={{
              ml: 0.5,
              fontWeight: 400,
              opacity: 0.6,
              color: "#000",
            }}
          >
            (Winner: {ev.winner})
          </Typography>
        </Typography>
      </Typography>

      {/* chips por jugador: verde si ganó, rojo si perdió */}
      {Array.isArray(ev.players) && ev.players.length > 0 && (
        <Box
          sx={{
            ml: 4.5,
            mt: 0.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          {ev.players.map((p, i) => (
            <Chip
              key={p.userId || p.nombre || i}
              size="small"
              label={`${p.nombre} ${p.won ? "🏆WON🏆" : "😢LOST😢"}`}
              color={p.won ? "success" : "error"} // verde si ganó, rojo si perdió
              sx={{ height: 22, fontWeight: 800 }}
            />
          ))}
        </Box>
      )}

      {/* línea con emojis (opcional, como ahora) */}
      <Typography
        variant="body2"
        sx={{
          color: "#000",
          fontWeight: 600,
          ml: 4.5,
          wordBreak: "break-word",
        }}
      >
        {vsLine}
      </Typography>

      <Box sx={{ ml: 4.5, mt: 0.5 }}>
        <Chip
          size="small"
          color="warning"
          label={`+Pool: ${fmt(ev.poolAdded || 0)} HUESO · Burned: ${fmt(
            ev.burnAdded || 0
          )} HUESO`}
          sx={{ height: 22, fontWeight: 800 }}
        />
      </Box>
    </Box>
  );
})}




                </Stack>
              )}
            </Paper>

            {/* === NUEVO: Season Reset / Payout Log === */}
            <Paper
              {...cardProps}
              sx={{
                ...cardProps.sx,
                maxHeight: 260,
                overflowY: "auto",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#000",
                  fontWeight: 700,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  rowGap: 1,
                }}
              >
                <span>Season Reset / Payout Log</span>
                {/* Si el backend manda la clave de temporada en el primer item */}
                {resetFeed?.[0]?.seasonKey && (
                  <Chip
                    size="small"
                    label={`Season: ${resetFeed[0].seasonKey}`}
                    sx={{ fontWeight: 700 }}
                    color="default"
                  />
                )}
              </Typography>

              {resetFeed.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.6, color: "#000" }}
                >
                  No reset or payout events yet.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {resetFeed.map((ev, idx) => {
                    const dateStr = new Date(ev.ts).toLocaleString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    });

                    // Etiqueta amigable según tipo/razón
                    const razon = ev.razon || ev.type || "EVENT";
                    let label = "";
                    if (["SEASON_RESET", "RESET"].includes(razon)) {
                      label = "Season reset";
                    } else if (["POOL_PAYOUT", "POOL"].includes(razon)) {
                      label = "Pool payout";
                    } else if (["VIP_PAYOUT", "VIP"].includes(razon)) {
                      label = `${vipSymbol} payout`;
                    } else if (["TICKETS_PAYOUT", "TICKETS_DISTRIBUTED"].includes(razon)) {
                      label = "Tickets distributed";
                    } else {
                      label = razon;
                    }

                    // Campos flexibles para cantidades (totales o por usuario)
                    const seasonKey = ev.seasonKey || ev.season || "-";
                    const huesoSent =
                      ev.huesoSent ?? ev.hueso ?? ev.pool ?? ev.amountHueso ?? 0;
                    const ticketsSent =
                      ev.ticketsSent ?? ev.tickets ?? ev.amountTickets ?? 0;
                    const vipSent =
                      ev.vipSent ?? ev.vip ?? ev.amountVip ?? 0;

                    // === NUEVO: tokens adicionales flexibles (por ejemplo SWAP.HIVE)
                    // Soporta: ev.tokens (array de {symbol, amount, decimals}),
                    //          ev.token (objeto único),
                    //          ev.swapTokens / ev.extraTokens (arrays alternativos).
                    const tokenItems = [
                      ...(Array.isArray(ev.tokens) ? ev.tokens : []),
                      ...(Array.isArray(ev.swapTokens) ? ev.swapTokens : []),
                      ...(Array.isArray(ev.extraTokens) ? ev.extraTokens : []),
                      ...(ev.token && typeof ev.token === "object" ? [ev.token] : []),
                    ].filter(Boolean);

                    const hasUser = !!ev.user; // línea por jugador
                    const note = ev.note || ev.detalle;

                    return (
                      <Box
                        key={idx}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          border: "1px solid rgba(0,0,0,0.1)",
                          backgroundColor: "rgba(255,255,255,0.6)",
                        }}
                      >
                        {/* Cabecera */}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: "#000",
                            display: "block",
                            mb: 0.25,
                          }}
                        >
                          {dateStr} • {label}
                          {seasonKey && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ ml: 1, opacity: 0.75 }}
                            >
                              (Season: {seasonKey})
                            </Typography>
                          )}
                          {hasUser && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ ml: 1, fontWeight: 800 }}
                            >
                              • {ev.user}
                            </Typography>
                          )}
                        </Typography>

                        {/* Montos */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#000",
                            fontWeight: hasUser ? 700 : 600,
                            ml: 0.5,
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "12px",
                          }}
                        >
                          🦴 HUESO: <b>{fmt(huesoSent)}</b>
                          <span style={{ display: "inline-flex", alignItems: "center" }}>
                            🎟️ Tickets: <b style={{ marginLeft: 4 }}>{fmt(ticketsSent)}</b>
                          </span>
                          <span>🍖 {vipSymbol}: <b>{fmtVip(vipSent)}</b></span>

                          {/* 3) NUEVO: lista de tokens adicionales (ej. SWAP.HIVE) */}
                          {tokenItems.map((tk, j) => {
                            const sym = tk?.symbol || SWAP_SYMBOL;
                            const amt =
                              typeof tk?.amount === "number"
                                ? tk.amount
                                : (typeof tk?.qty === "number" ? tk.qty : 0);
                            const dec = Number(
                              (typeof tk?.decimals === "number" ? tk.decimals : undefined) ??
                              SWAP_DECIMALS
                            );
                            return (
                              <span key={`${sym}-${j}`} style={{ display: "inline-flex", alignItems: "center" }}>
                                💱 {sym}: <b style={{ marginLeft: 4 }}>{fmtToken(amt, dec)}</b>
                              </span>
                            );
                          })}
                        </Typography>

                        {/* txid opcional (si viene del backend) */}
                        {ev.txid && (
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.75, color: "#000", ml: 0.5, display: "block", mt: 0.25 }}
                          >
                            tx:&nbsp;
                            <a
                              href={`https://he.dtools.dev/tx/${ev.txid}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {String(ev.txid).slice(0, 12)}…
                            </a>
                          </Typography>
                        )}

                        {/* Nota / detalle */}
                        {note && (
                          <Box sx={{ ml: 0.5, mt: 0.25 }}>
                            <Chip
                              size="small"
                              color="warning"
                              label={note}
                              sx={{ height:22,fontWeight:800 }}
                            />
                          </Box>
                        )}

                        {/* Extra opcional */}
                        {ev.extra && (
                          <Typography
                            variant="caption"
                            sx={{
                              opacity: 0.6,
                              color: "#000",
                              ml: 0.5,
                              display: "block",
                              mt: 0.25,
                              fontFamily: "monospace",
                              wordBreak: "break-word",
                            }}
                          >
                            {JSON.stringify(ev.extra)}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Leaderboard */}
      <Box sx={{ maxWidth: 1080, mx: "auto", mt: 1 }}>
        <Paper
          {...cardProps}
          sx={{
            ...cardProps.sx,
            backgroundColor: "rgba(0,0,0,0.4)",
            color: "#fff",
            boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 1,
              color: "#ffcc00",
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            🏆 Leaderboard
          </Typography>

          <Typography
            variant="body2"
            sx={{ opacity: 0.9, mb: 1, color: "#fff" }}
          >
            Season: <b>{season}</b> — Weekly Pool:{" "}
            <b>{fmt(pool)}</b> HUESO
          </Typography>

          {/* barra de unlock también acá */}
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: "rgba(0,0,0,0.5)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "#fff",
              maxWidth: 480,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "#fff" }}
              >
                Weekly Unlock
              </Typography>

              <Chip
                size="small"
                color={
                  unlockProgress.unlocked ? "success" : "error"
                }
                label={
                  unlockProgress.unlocked
                    ? "UNLOCKED"
                    : "LOCKED"
                }
                sx={{
                  fontWeight: 700,
                  color: "#fff",
                }}
              />
            </Stack>

            <Typography
              variant="body2"
              sx={{ mb: 1, color: "#fff" }}
            >
              Battles this week (global):{" "}
              <b>
                {fmt(unlockProgress.current)}/
                {fmt(unlockProgress.goal || 0)}
              </b>
            </Typography>

            <LinearProgress
              variant="determinate"
              value={unlockPct}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor:
                  "rgba(255,255,255,0.15)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 4,
                  backgroundColor: unlockProgress.unlocked
                    ? "rgba(76,175,80,0.9)"
                    : "rgba(255,235,59,0.9)",
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.5,
                opacity: 0.8,
                color: "#fff",
              }}
            >
              Pool distributes only if unlocked when the
              week resets.
            </Typography>
          </Box>

          <Box
            sx={{
              overflowX: "auto",
              borderRadius: 1,
              border:
                "1px solid rgba(255,255,255,0.3)",
              backgroundColor:
                "rgba(0,0,0,0.4)",
              color: "#fff",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "40px 1fr 70px 90px 90px 140px 140px 140px 140px",
                p: 1,
                bgcolor: "rgba(255,255,255,0.08)",
                color: "#fff",
                fontWeight: 700,
                borderBottom:
                  "1px solid rgba(255,255,255,0.2)",
              }}
            >
              <span>#</span>
              <span>🙍🏻‍♂️ Player</span>
              <span>🔝 MMR</span>
              <span>☄️ PP</span>
              <span>⚔️ Weekly Battles</span>
              <span>🦴 HUESO (preview)</span>
              <span>🎟️ Tickets (preview)</span>
              <span>🍖 {vipSymbol} (preview)</span>
              <span>💱 {swapSymbol} (preview)</span>

            </Box>

            {/* Rows */}
            {leader.length === 0 && (
              <Box
                sx={{
                  p: 2,
                  opacity: 0.7,
                  color: "#fff",
                }}
              >
                No positions yet
              </Box>
            )}

            {leader.map((r, i) => {
              const av = getAvatarForRow(r);
              return (
                <Box
                  key={r.userId || `${r.nombre}-${i}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "40px 1fr 70px 90px 90px 140px 140px 140px 140px",
                    alignItems: "center",
                    p: 1,
                    borderTop:
                      "1px solid rgba(255,255,255,0.12)",
                    color: "#fff",
                    bgcolor:
                      i % 2 === 0
                        ? "rgba(255,255,255,0.07)"
                        : "rgba(255,255,255,0.03)",
                  }}
                >
                  {/* rank */}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600 }}
                  >
                    {i + 1}
                  </Box>

                  {/* player avatar + name */}
                  <Box
                    component="span"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "6px",
                        overflow: "hidden",
                        border:
                          "1px solid rgba(147, 145, 145, 0.21)",
                        flexShrink: 0,
                        backgroundColor: "#000",
                      }}
                    >
                      <img
                        src={av.img}
                        alt={av.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                        onError={(e) => {
                          e.currentTarget.src =
                            AVATARS[0].img;
                        }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#fff",
                        fontWeight: 500,
                        textOverflow:
                          "ellipsis",
                        overflow: "hidden",
                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      {r.nombre}
                    </Typography>
                  </Box>

                  {/* mmr */}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 500,
                      textAlign: "left",
                    }}
                  >
                    {fmt(r?.mmr || 0)}
                  </Box>

                  {/* PP */}
                  <Box 
                    component="span" 
                    sx={{ 
                      fontWeight: 500, 
                      textAlign: "left",
                    }}
                  >
                    {fmt(getRowPP(r))}
                  </Box>

                  {/* weekly battles */}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 500,
                      textAlign: "left",
                    }}
                  >
                    {fmt(r?.battlesThisWeek || 0)}
                  </Box>

                  {/* preview hueso */}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 500,
                      textAlign: "left",
                    }}
                  >
                    {fmt(r?.preview?.hueso || 0)}
                  </Box>

                  {/* preview tickets */}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 500,
                      textAlign: "left",
                    }}
                  >
                    {fmt(r?.preview?.tickets || 0)}
                  </Box>

                  {/* VIP semanal (preview/calculado por backend) */}
                  <Box
                    component="span" 
                    sx={{ fontWeight: 600, textAlign: "left" }}
                  >
                    {fmtVip(r?.vip ?? r?.preview?.vip)}
                  </Box>

                  {/* SWAP preview */}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, textAlign: "left" }}
                  >
                    {getRowSwapPreview(r, swapDecimals, swapSymbol)}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      </Box>

      {/* Battle Modal */}
      <Dialog
        open={battleOpen}
        onClose={() => {
          if (battlePhase === "result") {
            setBattleOpen(false);
            setAnimDone(false);
            setResultReady(false);
            resultBufferRef.current = null;
            if (animTimerRef.current)
              clearTimeout(animTimerRef.current);
            loadStatus();
            loadFeed();
            loadBattleFeed();
            loadResetFeed();
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            color: "#fffefeff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {battlePhase === "anim"
            ? "Battle in Progress..."
            : battleResult?.title || "Result"}
          <Box>
            <IconButton
              onClick={() => {
                setMuted((m) => !m);
                const v = videoRef.current;
                if (v) v.muted = !v.muted;
              }}
              size="small"
            >
              {muted ? (
                <VolumeOffIcon />
              ) : (
                <VolumeUpIcon />
              )}
            </IconButton>
            <IconButton
              onClick={() => {
                const v = videoRef.current;
                if (v)
                  v.play().catch(() => {});
              }}
              size="small"
            >
              <PlayArrowIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ color: "#000" }}>
          {battlePhase === "anim" ? (
            <Box>
              <Box
                sx={{
                  width: "100%",
                  height: 260,
                  mb: 2,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid #ddd",
                  background: "#c9c6c6ff",
                }}
              >
                <video
                  ref={videoRef}
                  src={BATTLE_ANIM}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  autoPlay
                  controls
                  playsInline
                />
              </Box>
              <LinearProgress />
              <Typography
                sx={{
                  mt: 1,
                  textAlign: "center",
                  fontWeight: 600,
                }}
              >
                {animStatusText} {battleCountdown}s
              </Typography>
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: "0.8rem",
                  opacity: 0.7,
                }}
              >
                (Tickets x2 if PvP win)
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* VS CARD */}
              {battleResult?.detail?.matchup && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid #ffffffff",
                    backgroundColor: "rgba(240, 235, 235, 0.03)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 800 }}
                  >
                    Matchup
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1.5}
                  >
                    {/* Me */}
                    <Stack
                      spacing={0.5}
                      alignItems="center"
                      sx={{ minWidth: 120, flex: 1 }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: "10px",
                          overflow: "hidden",
                          border: "1px solid #ddd",
                          background: "#c7c7c7ff",
                        }}
                      >
                        <img
                          src={battleResult.detail.matchup.me.avatarUrl || AVATARS[0].img}
                          alt="me"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => { e.currentTarget.src = AVATARS[0].img; }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {battleResult.detail.matchup.me.name || "You"}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        MMR: {fmt(battleResult.detail.matchup.me.mmr || 0)}
                      </Typography>
                    </Stack>

                    {/* VS */}
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 900, mx: 1 }}
                    >
                      VS
                    </Typography>

                    {/* Opp */}
                    <Stack
                      spacing={0.5}
                      alignItems="center"
                      sx={{ minWidth: 120, flex: 1 }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: "10px",
                          overflow: "hidden",
                          border: "1px solid #ddd",
                          background: "#c3c2c2ff",
                        }}
                      >
                        <img
                          src={battleResult.detail.matchup.opp.avatarUrl || AVATARS[0].img}
                          alt="opp"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => { e.currentTarget.src = AVATARS[0].img; }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {battleResult.detail.matchup.opp.name || "Opponent"}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        MMR: {fmt(battleResult.detail.matchup.opp.mmr || 0)}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 1, opacity: 0.8, textAlign: "center" }}
                  >
                    Winner: <b>{battleResult?.detail?.winnerName || (battleResult?.detail?.won ? "You" : "Opponent")}</b>
                  </Typography>
                </Box>
              )}

              <Typography
                variant="body1"
                sx={{ mb: 1 }}
              >
                {battleResult?.detail?.won
                  ? "✅ You won!"
                  : "❌ You lost"}{" "}
                — Rounds:{" "}
                {battleResult?.detail?.rounds ?? 3}
              </Typography>
              <Divider sx={{ my: 1 }} />
              {/* Power change summary */}
              {battleResult?.detail?.pp && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5 }}
                  >
                    Power Change
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2">
                        Before:{" "}
                        <b>
                          {fmt(
                            battleResult.detail.pp
                              .before
                          )}
                        </b>
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2">
                        Consumed:{" "}
                        <b>
                          {fmt(
                            battleResult.detail.pp
                              .consumed
                          )}
                        </b>
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2">
                        Refund:{" "}
                        <b>
                          {fmt(
                            battleResult.detail.pp
                              .refund
                          )}
                        </b>
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2">
                        Final:{" "}
                        <b>
                          {fmt(
                            battleResult.detail.pp
                              .final
                          )}
                        </b>
                      </Typography>
                    </Grid>
                  </Grid>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      opacity: 0.8,
                    }}
                  >
                    Net loss:{" "}
                    <b>
                      {fmt(
                        battleResult.detail.pp
                          .lost
                      )}
                    </b>{" "}
                    PP
                  </Typography>
                </Box>
              )}
              <Stack direction="row" spacing={2}>
                <Button
                  {...btnPrimary}
                  onClick={async () => {
                    setBattleOpen(false);
                    setAnimDone(false);
                    setResultReady(false);
                    resultBufferRef.current =
                      null;
                    if (animTimerRef.current)
                      clearTimeout(
                        animTimerRef.current
                      );
                    await loadStatus();
                    await loadFeed();
                    await loadBattleFeed();
                    await loadResetFeed();
                  }}
                >
                  Close
                </Button>
                <Button
                  {...btnGhost}
                  onClick={async () => {
                    await loadLeaderboard();
                    await loadFeed();
                    await loadBattleFeed();
                    await loadResetFeed();
                  }}
                >
                  View Ranking
                </Button>
              </Stack>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Reward Modal */}
      <Dialog
        open={rewardOpen}
        onClose={() => {
          setRewardOpen(false);
          loadStatus();
          loadFeed();
          loadBattleFeed();
          loadResetFeed();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: "#ffffffff" }}>
          🎉 Reward
        </DialogTitle>
        <DialogContent sx={{ color: "#000" }}>
          {rewardInfo ? (
            <>
              {(Array.isArray(rewardInfo)
                ? rewardInfo
                : [rewardInfo]
              ).map((rew, idx) => (
                <Box
                  key={idx}
                  sx={{
                    mb: 2,
                    textAlign: "center",
                  }}
                >
                  {rew.type === "VIPHUESO" && (
                    <Typography
                      variant="h6"
                      sx={{ mb: 1 }}
                    >
                      You received{" "}
                        <b>
                        {fmtVip(rew.amount)}
                        </b>
                         {vipSymbol}!
                    </Typography>
                  )}

                  {rew.type === "TICKET" && (
                    <>
                      <img
                        src={TICKET_GIF}
                        alt="Ticket Reward"
                        style={{
                          width: 120,
                          height: 120,
                          objectFit: "contain",
                          display:
                            "block",
                          margin:
                            "0 auto 8px",
                        }}
                      />
                      <Typography
                        variant="h6"
                        align="center"
                      >
                        You received{" "}
                        <b>
                          {fmt(
                            rew.amount
                          )}
                        </b>{" "}
                        Ticket
                        {Number(
                          rew.amount
                        ) === 1
                          ? ""
                          : "s"}{" "}
                        (Win reward)!
                      </Typography>
                    </>
                  )}

                  {rew.type === "ACTIVITY" && (
                    <>
                      <img
                        src={TICKET_GIF}
                        alt="Activity Ticket"
                        style={{
                          width: 120,
                          height: 120,
                          objectFit: "contain",
                          display:
                            "block",
                          margin:
                            "0 auto 8px",
                        }}
                      />
                      <Typography
                        variant="h6"
                        align="center"
                      >
                        Activity bonus!{" "}
                        <b>
                          {fmt(
                            rew.amount
                          )}
                        </b>{" "}
                        Ticket
                        {Number(
                          rew.amount
                        ) === 1
                          ? ""
                          : "s"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.8,
                          textAlign:
                            "center",
                        }}
                      >
                        You've played{" "}
                        {fmt(
                          rew.played ||
                            0
                        )}{" "}
                        battles this
                        week.
                      </Typography>
                    </>
                  )}
                </Box>
              ))}

              <Typography
                variant="body2"
                sx={{
                  opacity: 0.8,
                  mb: 2,
                  textAlign: "center",
                }}
              >
                Rewards are delivered by
                the same account used for
                tickets. Check your
                wallet / inventory.
              </Typography>

              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
              >
                <Button
                  {...btnPrimary}
                  onClick={() => {
                    setRewardOpen(false);
                    loadStatus();
                    loadFeed();
                    loadBattleFeed();
                    loadResetFeed();
                  }}
                >
                  Great!
                </Button>
                <Button
                  {...btnGhost}
                  onClick={() => {
                    loadStatus();
                    loadFeed();
                    loadBattleFeed();
                    loadResetFeed();
                  }}
                >
                  Refresh Balance
                </Button>
              </Stack>
            </>
          ) : (
            <Typography>
              No reward info.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={closeSnack}
      >
        <Alert
          onClose={closeSnack}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
